/**
 * Marca de Middle, el monitor de integraciones de Grupo Amoblé.
 *
 * Concepto: Middle es el middleware que está al medio de Janis, SAP, Multivende,
 * HubSpot, Syncro y DecoraPOS. La "M" es un solo recorrido, como un cable, y su
 * vértice central es un nodo ámbar: la mitad izquierda y la derecha solo se
 * tocan a través del medio, y el aire alrededor del nodo lo deja a la vista. La
 * baldosa índigo sólida es lo que hace que el ícono se encuentre de un vistazo en
 * una barra de pestañas llena.
 *
 * Es el mismo dibujo que `public/favicon.svg`, en la misma grilla de 64 (a 16 y
 * 32 px los trazos caen justo en píxeles enteros). Si cambias uno, cambia el
 * otro: `MiddleLogo.test.tsx` los compara.
 */
import type { ReactElement } from 'react';

/** Paleta de la marca. */
const TILE = '#4f46e5';
const INK = '#ffffff';
const NODE = '#f59e0b';

export interface MiddleMarkProps {
  /** Lado en píxeles; por defecto 32. */
  readonly size?: number | undefined;
  /** Nombre accesible. Sin él la marca es decorativa (va junto al texto "Middle"). */
  readonly title?: string | undefined;
  readonly className?: string | undefined;
}

export function MiddleMark({ size = 32, title, className }: MiddleMarkProps): ReactElement {
  const named = title !== undefined && title.trim() !== '';

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role={named ? 'img' : undefined}
      aria-hidden={named ? undefined : true}
      focusable={named ? undefined : 'false'}
    >
      {named ? <title>{title}</title> : null}
      <rect width="64" height="64" rx="14" fill={TILE} />
      <path
        d="M16 48V16L32 40L48 16V48"
        fill="none"
        stroke={INK}
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Aire alrededor del nodo: separa los trazos del ámbar. */}
      <circle cx="32" cy="40" r="10" fill={TILE} />
      <circle cx="32" cy="40" r="7.5" fill={NODE} />
    </svg>
  );
}
